import {
  ActionBar,
  Button,
  Filter,
  IconButton,
  InputAmount,
  InputWrapper,
} from '@vrplatform/components';
import { getApiErrorMessage, useApiQuery, useTeam } from '@vrplatform/data';
import { type MRT_ColumnDef, LazyTable } from '@vrplatform/table';
import {
  Box,
  Center,
  Checkbox,
  Group,
  HoverCard,
  rem,
  Stack,
  Text,
} from '@mantine/core';
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Controller,
  type UseFieldArrayReturn,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { bankersRoundToCents, day, formatCurrency } from '@vrplatform/utils';
import { Icon } from '@vrplatform/icons';
import type { AdvancedDepositsModalFormInputs } from './_types';
import {
  getCurrencySymbol,
  getTransactionLineType,
} from '@vrplatform/ui-common';
import { Skeleton } from '@mantine/core';
import { AppIconById } from '../../components';

const useModalForm = () => useFormContext<AdvancedDepositsModalFormInputs>();

type AdvancedDepositsModalTableFilter = {
  search: string;
} | null;

type AdvancedDepositsModalReservationField = UseFieldArrayReturn<
  AdvancedDepositsModalFormInputs,
  'reservations',
  'id'
>['fields'][number];

export const AdvancedDepositsModalTable = ({
  openSelectReservationModal,
  openCsvImportModal,
  csvImportLoading,
  fields,
  remove,
}: {
  openSelectReservationModal: () => void;
  openCsvImportModal: () => void;
  csvImportLoading?: boolean;
} & UseFieldArrayReturn<
  AdvancedDepositsModalFormInputs,
  'reservations',
  'id'
>) => {
  const [{ currency }] = useTeam();

  const [filter, setFilter] = useState<AdvancedDepositsModalTableFilter>(null);
  const [selectedReservationIds, setSelectedReservationIds] = useState<
    Record<string, boolean>
  >({});

  const reservationIds = useMemo(
    () => fields.map((reservation) => reservation.reservationId),
    [fields]
  );

  const selectedCount = useMemo(
    () =>
      reservationIds.filter(
        (reservationId) => selectedReservationIds[reservationId]
      ).length,
    [reservationIds, selectedReservationIds]
  );

  const allReservationsSelected =
    reservationIds.length > 0 && selectedCount === reservationIds.length;
  const someReservationsSelected =
    selectedCount > 0 && !allReservationsSelected;

  useEffect(() => {
    setSelectedReservationIds((currentSelected) => {
      if (!Object.keys(currentSelected).length) return currentSelected;

      const reservationIdSet = new Set(reservationIds);
      let changed = false;

      const nextSelected = Object.entries(currentSelected).reduce<
        Record<string, boolean>
      >((result, [reservationId, selected]) => {
        if (!selected) {
          changed = true;
          return result;
        }

        if (!reservationIdSet.has(reservationId)) {
          changed = true;
          return result;
        }

        result[reservationId] = true;
        return result;
      }, {});

      return changed ? nextSelected : currentSelected;
    });
  }, [reservationIds]);

  const setReservationSelected = useCallback(
    (reservationId: string, selected: boolean) => {
      setSelectedReservationIds((currentSelected) => {
        if (selected) {
          if (currentSelected[reservationId]) return currentSelected;

          return {
            ...currentSelected,
            [reservationId]: true,
          };
        }

        if (!currentSelected[reservationId]) return currentSelected;

        const nextSelected = { ...currentSelected };
        delete nextSelected[reservationId];
        return nextSelected;
      });
    },
    []
  );

  const clearSelection = useCallback(() => {
    setSelectedReservationIds({});
  }, []);

  const setAllReservationsSelected = useCallback(
    (selected: boolean) => {
      if (!selected) {
        clearSelection();
        return;
      }

      setSelectedReservationIds(
        Object.fromEntries(
          reservationIds.map((reservationId) => [reservationId, true])
        )
      );
    },
    [clearSelection, reservationIds]
  );

  const removeSingleReservation = useCallback(
    (reservationIndex: number, reservationId: string) => {
      remove(reservationIndex);

      setSelectedReservationIds((currentSelected) => {
        if (!currentSelected[reservationId]) return currentSelected;

        const nextSelected = { ...currentSelected };
        delete nextSelected[reservationId];
        return nextSelected;
      });
    },
    [remove]
  );

  const removeSelectedReservations = useCallback(() => {
    if (!selectedCount) return;

    const indexesToRemove = fields.reduce<number[]>(
      (result, reservation, reservationIndex) => {
        if (!selectedReservationIds[reservation.reservationId]) return result;

        result.push(reservationIndex);
        return result;
      },
      []
    );

    if (!indexesToRemove.length) return;

    remove(indexesToRemove);
    clearSelection();
  }, [clearSelection, fields, remove, selectedCount, selectedReservationIds]);

  const currencySymbol = getCurrencySymbol(currency).replaceAll(
    /[A-Za-z]/g,
    ''
  );

  const columns = useMemo<
    MRT_ColumnDef<AdvancedDepositsModalReservationField>[]
  >(
    () => [
      {
        header: 'Reservation',
        Header: () => (
          <Group gap="xs" wrap="nowrap">
            <Checkbox
              aria-label="Select all reservations"
              checked={allReservationsSelected}
              indeterminate={someReservationsSelected}
              onChange={(event) =>
                setAllReservationsSelected(event.currentTarget.checked)
              }
            />
            <Text c="gray.7" size="xs">
              Reservation
            </Text>
          </Group>
        ),
        Cell: ({ row }) => {
          const reservation = row.original;
          const reservationLabel = [
            reservation?.guestName,
            reservation?.confirmationCode,
          ]
            .filter(Boolean)
            .join(', ');

          return (
            <Group gap="xs" wrap="nowrap">
              <Checkbox
                aria-label={`Select reservation ${
                  reservationLabel || reservation.reservationId
                }`}
                checked={Boolean(
                  selectedReservationIds[reservation.reservationId]
                )}
                onChange={(event) =>
                  setReservationSelected(
                    reservation.reservationId,
                    event.currentTarget.checked
                  )
                }
              />
              <Box>
                <Text>{reservationLabel}</Text>
                <Text size="xs" c="gray">
                  {[
                    day(reservation?.checkIn).format('MMM D'),
                    day(reservation?.checkOut).format('MMM D, YYYY'),
                  ]
                    .filter(Boolean)
                    .join(' - ')}
                </Text>
              </Box>
            </Group>
          );
        },
      },
      {
        header: 'Reservation Amount',
        mantineTableHeadCellProps: {
          align: 'right',
          sx: (theme) => ({
            fontWeight: 400,
            color: theme.colors.gray[7],
            textAlign: 'right',
          }),
        },
        mantineTableBodyCellProps: {
          align: 'right',
          sx: {
            textAlign: 'right',
          },
        },
        accessorFn: (row) =>
          formatCurrency((row?.reservationCentTotal ?? 0) / 100, currency),
      },
      {
        header: 'Synced Payments/Refunds',
        mantineTableHeadCellProps: {
          align: 'right',
          sx: (theme) => ({
            fontWeight: 400,
            color: theme.colors.gray[7],
            textAlign: 'right',
          }),
        },
        mantineTableBodyCellProps: {
          align: 'right',
          sx: {
            textAlign: 'right',
          },
        },
        Cell: ({ row }) => {
          const reservation = row.original;

          const transactionIds = reservation.synced.transactionIds;
          const disabled = !transactionIds.length;

          const total =
            reservation.synced.paymentCentTotal +
            reservation.synced.refundCentTotal;

          return (
            <HoverCard
              disabled={disabled}
              withArrow
              position="bottom-end"
              arrowOffset={15}
              shadow="md"
            >
              <HoverCard.Target>
                <Text
                  sx={{
                    textDecoration: disabled ? 'none' : 'underline',
                    textDecorationColor: 'gray',
                    textUnderlineOffset: '5px',
                    textDecorationThickness: '1px',
                    textDecorationStyle: 'dotted',
                  }}
                >
                  {formatCurrency(total / 100, currency)}
                </Text>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <SyncedDepositsPopoverContent
                  transactionIds={transactionIds}
                  reservationId={reservation.reservationId}
                />
              </HoverCard.Dropdown>
            </HoverCard>
          );
        },
      },
      {
        header: 'Manual Payments',
        maxSize: 150,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          const methods = useModalForm();

          return (
            <Controller
              control={methods.control}
              name={`reservations.${row.index}.paymentCentTotal`}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper error={fieldState.error?.message}>
                    <InputAmount
                      value={
                        field.value === undefined
                          ? ''
                          : Number(field.value || '0') / 100
                      }
                      setValue={(value) => {
                        if (typeof value === 'string' && value === '') {
                          field.onChange(0);
                          return;
                        }

                        field.onChange(bankersRoundToCents(value));
                      }}
                      decimalScale={2}
                      error={!!fieldState.error?.message}
                      placeholder="0.00"
                      leftSection={<span>{currencySymbol}</span>}
                      hideControls
                      size="xs"
                      w={100}
                    />
                  </InputWrapper>
                );
              }}
            />
          );
        },
      },
      {
        header: 'Manual Refunds',
        maxSize: 130,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          const methods = useModalForm();

          return (
            <Controller
              control={methods.control}
              name={`reservations.${row.index}.refundCentTotal`}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper error={fieldState.error?.message}>
                    <InputAmount
                      value={
                        field.value === undefined
                          ? ''
                          : Number(field.value || '0') / 100
                      }
                      setValue={(value) => {
                        if (typeof value === 'string' && value === '') {
                          field.onChange(0);
                          return;
                        }

                        field.onChange(bankersRoundToCents(value));
                      }}
                      decimalScale={2}
                      error={!!fieldState.error?.message}
                      placeholder="0.00"
                      leftSection={<span>{currencySymbol}</span>}
                      hideControls
                      size="xs"
                      w={100}
                    />
                  </InputWrapper>
                );
              }}
            />
          );
        },
      },
      {
        header: 'Total',
        maxSize: 100,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
          sx: {
            textAlign: 'right',
          },
        },
        Cell: ({ row }) => {
          const methods = useModalForm();

          const refundCentTotal = useWatch({
            control: methods.control,
            name: `reservations.${row.index}.refundCentTotal`,
          });

          const paymentCentTotal = useWatch({
            control: methods.control,
            name: `reservations.${row.index}.paymentCentTotal`,
          });

          const syncedCentTotal =
            row.original.synced.paymentCentTotal +
            row.original.synced.refundCentTotal;

          const total = syncedCentTotal + paymentCentTotal - refundCentTotal;

          return <Text>{formatCurrency(total / 100, currency)}</Text>;
        },
      },
      {
        header: 'Remove Reservation',
        Header: '',
        maxSize: 30,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => (
          <IconButton
            icon="TrashIcon"
            iconSize={18}
            onClick={() =>
              removeSingleReservation(row.index, row.original.reservationId)
            }
          />
        ),
      },
    ],
    [
      allReservationsSelected,
      currency,
      currencySymbol,
      removeSingleReservation,
      selectedReservationIds,
      setAllReservationsSelected,
      setReservationSelected,
      someReservationsSelected,
    ]
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <Group justify="space-between" px="sm" mb="xs" pt={2}>
        <TableFilter filter={filter} setFilter={setFilter} />

        <Group gap="xs">
          <Button
            size="xs"
            leftIcon="CloudUploadIcon"
            onClick={openCsvImportModal}
            loading={csvImportLoading}
          >
            Upload CSV
          </Button>
          <Button
            size="xs"
            leftIcon="PlusIcon"
            onClick={openSelectReservationModal}
          >
            Add reservation
          </Button>
        </Group>
      </Group>

      <LazyTable
        data={{
          rows: fields,
          error: undefined,
          loading: false,
          rowCount: fields.length,
        }}
        table={{
          hideSettings: true,
          emptyRowsFallback: 'No reservations selected',
          hidePagination: true,
          hideTopBar: true,
          columns,
        }}
      />

      <ActionBar
        mounted={selectedCount > 0}
        withinPortal={false}
        maxWidth={500}
        zIndex={0}
        sx={{
          bottom: 80,
        }}
      >
        <Group gap="xs" wrap="nowrap">
          <Text c="dark.4" size="sm">
            {selectedCount} selected
          </Text>
          <IconButton
            icon="CrossIcon"
            iconSize={14}
            aria-label="Clear selected reservations"
            onClick={clearSelection}
          />
        </Group>
        <Button
          size="xs"
          leftIcon="TrashIcon"
          onClick={removeSelectedReservations}
        >
          Remove
        </Button>
      </ActionBar>
    </Box>
  );
};

const SyncedDepositsPopoverContent = ({
  transactionIds,
  reservationId,
}: {
  transactionIds: string[];
  reservationId: string;
}) => {
  const queryData = useApiQuery(
    async (api, signal, args) => {
      const result = await api.GET('/transactions', {
        params: {
          query: {
            ids: args.transactionIds.join(','),
            includeLines: true,
          },
        },
        signal,
      });
      if (result.error) throw result.error;

      return result.data.data;
    },
    {
      variables: {
        transactionIds,
      },
      skip: !transactionIds.length,
      queryGroup: 'deposits',
      uniqueQueryKey: 'advanced-deposits-modal-table',
    }
  );

  if (queryData.isLoading)
    return (
      <Stack>
        <Skeleton h={15} w={50} />
        <Skeleton h={15} w={50} />
      </Stack>
    );

  if (queryData.error)
    return (
      <Stack>
        <Icon icon="AlertTriangleIcon" size={18} color="red" />
        <Text>
          {getApiErrorMessage(
            queryData.error,
            'Failed to load synced deposits'
          )}
        </Text>
      </Stack>
    );

  return (
    <Stack>
      {queryData.data?.map((deposit) => {
        const lines =
          deposit.lines
            ?.map((line) => {
              const type = getTransactionLineType({
                accountAssignmentType: line.assignment,
                centTotal: line.amount,
              });

              return {
                type,
                centTotal: line.amount,
                account: line.account,
                reservationId: line.reservation?.id,
              };
            })
            .filter((x) => {
              const isType = x.type === 'payment' || x.type === 'refund';
              const isReservationId = x.reservationId === reservationId;

              return isType && isReservationId;
            }) ?? [];

        const total = lines.reduce((acc, line) => {
          if (line.type === 'payment') {
            return acc + line.centTotal;
          }

          if (line.type === 'refund') {
            return acc + line.centTotal;
          }

          return acc;
        }, 0);

        return (
          <Group key={deposit.id} justify="space-between" wrap="nowrap">
            <Text miw={100}>{day(deposit.date).format('LL')}</Text>
            <Center miw={100}>
              <Group
                wrap="nowrap"
                gap={5}
                sx={(theme) => ({
                  backgroundColor: theme.colors.gray[1],
                  border: `1px solid ${theme.colors.gray[2]}`,
                  paddingInline: rem(6),
                  paddingBlock: rem(3),
                  borderRadius: theme.radius.xl,
                })}
              >
                {deposit?.connection?.appId && (
                  <AppIconById appId={deposit?.connection?.appId} />
                )}
                <Text span size="xs">
                  {deposit?.connection?.appId === 'csvImport'
                    ? 'CSV'
                    : deposit?.connection?.name || 'Manual'}
                </Text>
              </Group>
            </Center>
            <Text miw={100} ta="right">
              {formatCurrency(total / 100, deposit.currency)}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
};

const TableFilter = ({
  filter,
  setFilter,
}: {
  filter: AdvancedDepositsModalTableFilter;
  setFilter: (filter: SetStateAction<AdvancedDepositsModalTableFilter>) => void;
}) => {
  return (
    <Group gap="xs">
      <Filter.Search
        value={filter?.search || ''}
        setValue={(v) => setFilter({ search: v })}
      />
    </Group>
  );
};
